import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Heart, Lightbulb, Sparkles, TrendingUp, Crown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

interface DemographicInsightsProps {
  isPremium: boolean;
  onUpgrade: () => void;
  demographicScores: any;
}

const DemographicInsights: React.FC<DemographicInsightsProps> = ({ isPremium, onUpgrade, demographicScores }) => {
  const getPreference = (score: number) => {
    if (score >= 8.5) return 'highest';
    if (score >= 7.5) return 'high';
    if (score >= 6.5) return 'above-average';
    return 'average';
  };

  const demographics = [
    { group: "Latina Women", score: demographicScores.latina, preference: getPreference(demographicScores.latina), free: false },
    { group: "Asian Women", score: demographicScores.asian, preference: getPreference(demographicScores.asian), free: false },
    { group: "Middle Eastern Women", score: demographicScores.middleEastern, preference: getPreference(demographicScores.middleEastern), free: false },
    { group: "White Women", score: demographicScores.white, preference: getPreference(demographicScores.white), free: false },
    { group: "Black Women", score: demographicScores.black, preference: getPreference(demographicScores.black), free: false },
    { group: "South Asian Women", score: demographicScores.southAsian, preference: getPreference(demographicScores.southAsian), free: false },
  ];

  // Sort to find highest scoring demographics
  const sortedDemographics = [...demographics].sort((a, b) => b.score - a.score);
  
  // Show only 3rd highest as free preview (to create curiosity about top 2)
  const freePreviewDemographic = sortedDemographics[2];
  const lockedDemographics = sortedDemographics.filter((_, i) => i !== 2);

  const improvementTips = [
    {
      demographic: "Latina Women",
      tips: [
        "Grow a light beard or well-groomed stubble - 73% preference increase",
        "Style hair with texture and volume - Mediterranean look",
        "Wear fitted clothing that shows physique",
        "Learn basic Spanish phrases - shows cultural appreciation"
      ],
      icon: "💃"
    },
    {
      demographic: "Asian Women", 
      tips: [
        "Maintain clean-shaven or minimal facial hair",
        "Focus on skincare routine - clear skin is highly valued",
        "Dress in smart-casual style with attention to detail",
        "Show ambition and career focus in profile"
      ],
      icon: "🌸"
    },
    {
      demographic: "Middle Eastern Women",
      tips: [
        "Well-groomed beard is a major plus",
        "Dress sophisticatedly - avoid overly casual looks",
        "Show family values and respect for traditions",
        "Maintain good posture and confident body language"
      ],
      icon: "✨"
    },
    {
      demographic: "White Women",
      tips: [
        "Versatile style - clean-cut or rugged both work",
        "Show outdoor/adventure interests",
        "Maintain athletic or fit physique",
        "Smile genuinely in photos - warmth is key"
      ],
      icon: "🌟"
    },
    {
      demographic: "Black Women",
      tips: [
        "Show confidence and authenticity",
        "Well-maintained facial hair or clean-shaven",
        "Dress with personal style - express yourself",
        "Demonstrate emotional intelligence and communication skills"
      ],
      icon: "👑"
    }
  ];

  const ageGroups = [
    { range: "18-24", score: 7.2, free: false },
    { range: "25-34", score: 8.1, free: false },
    { range: "35-44", score: 7.8, free: false },
    { range: "45+", score: 7.5, free: false },
  ];

  return (
    <div className="space-y-6">
      {/* Ethnicity Preferences */}
      <Card className="p-6 bg-white/80 backdrop-blur-sm">
        <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Heart className="h-5 w-5 text-pink-600" />
          Preference by Ethnicity
        </h4>
        
        <div className="space-y-4">
          {/* Show only ONE demographic as free preview (3rd highest) */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="relative"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-700">{freePreviewDemographic.group}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-purple-600">{freePreviewDemographic.score}/10</span>
                {freePreviewDemographic.preference === 'high' && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    High Match
                  </span>
                )}
                {freePreviewDemographic.preference === 'above-average' && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                    Good Match
                  </span>
                )}
              </div>
            </div>
            <Progress value={freePreviewDemographic.score * 10} className="h-2" />
          </motion.div>

          {/* All other demographics are locked */}
          {lockedDemographics.map((demo, index) => (
            <motion.div
              key={demo.group}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: (index + 1) * 0.1 }}
              className="relative"
            >
              <div 
                className="absolute inset-0 bg-gradient-to-r from-purple-50 to-pink-50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg cursor-pointer hover:from-purple-100 hover:to-pink-100 transition-colors border-2 border-purple-200"
                onClick={onUpgrade}
              >
                <div className="text-center">
                  <Lock className="h-5 w-5 text-purple-600 mx-auto mb-1" />
                  <p className="text-xs font-bold text-purple-600">Unlock Premium</p>
                </div>
              </div>
              
              <div className="blur-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-700">{demo.group}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-purple-600">{demo.score}/10</span>
                    {demo.preference === 'highest' && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold">
                        🔥 Highest Match
                      </span>
                    )}
                  </div>
                </div>
                <Progress value={demo.score * 10} className="h-2" />
              </div>
            </motion.div>
          ))}
        </div>
      </Card>

      {/* Clickbait Teaser - Top Demographics */}
      {!isPremium && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Card 
            className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 cursor-pointer hover:shadow-lg transition-all"
            onClick={onUpgrade}
          >
            <div className="flex items-start gap-4">
              <div className="text-4xl">🔥</div>
              <div className="flex-1">
                <h4 className="font-bold text-lg mb-2 text-purple-900">
                  Unlock Your Top 5 Matches!
                </h4>
                <p className="text-gray-700 mb-3">
                  You've seen one demographic ({freePreviewDemographic.group} - {freePreviewDemographic.score}/10), but there are <strong>5 MORE demographics</strong> waiting to be revealed! 
                  Discover which women find you MOST attractive and get personalized strategies for each group.
                </p>
                <div className="bg-white rounded-lg p-3 mb-3">
                  <p className="text-sm text-gray-700 font-semibold mb-2">🎁 Premium Unlocks:</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>✓ See ALL 6 demographic ratings</li>
                    <li>✓ Discover your #1 and #2 best matches</li>
                    <li>✓ Get specific attraction tips for each group</li>
                    <li>✓ Age group preferences (18-24, 25-34, 35-44, 45+)</li>
                  </ul>
                </div>
                <div className="flex items-center gap-2 text-sm text-purple-600 font-semibold">
                  <Crown className="h-4 w-4" />
                  <span>Unlock all demographics for just $9.99</span>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Improvement Tips (Premium Only) */}
      {isPremium && (
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200">
          <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-yellow-600" />
            Personalized Attraction Tips
          </h4>
          
          <div className="space-y-6">
            {improvementTips.map((tip, index) => (
              <motion.div
                key={tip.demographic}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-4 shadow-sm"
              >
                <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="text-2xl">{tip.icon}</span>
                  How to Attract {tip.demographic}
                </h5>
                <ul className="space-y-2">
                  {tip.tips.map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <TrendingUp className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </Card>
      )}

      {/* Age Group Preferences */}
      <Card className="p-6 bg-white/80 backdrop-blur-sm relative">
        {!isPremium && (
          <div 
            className="absolute inset-0 bg-white/90 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg cursor-pointer hover:bg-white/95 transition-colors"
            onClick={onUpgrade}
          >
            <div className="text-center">
              <Lock className="h-8 w-8 text-purple-600 mx-auto mb-2" />
              <p className="font-semibold text-purple-600">Unlock Age Group Analysis</p>
              <p className="text-sm text-gray-600 mt-1">See which age ranges prefer you most</p>
            </div>
          </div>
        )}
        
        <div className={!isPremium ? 'blur-sm' : ''}>
          <h4 className="font-semibold text-lg mb-4">Preference by Age Group</h4>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {ageGroups.map((age) => (
              <div key={age.range} className="text-center">
                <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl p-4 mb-2">
                  <p className="text-2xl font-bold text-purple-600">{age.score}</p>
                </div>
                <p className="text-sm font-medium text-gray-700">{age.range}</p>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Insights Summary */}
      <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
        <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          Key Insights
        </h4>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-purple-600 font-bold">✓</span>
            <span>You scored <strong>{freePreviewDemographic.score}/10 with {freePreviewDemographic.group}</strong></span>
          </li>
          {!isPremium && (
            <>
              <li className="flex items-start gap-2 mt-4 pt-4 border-t border-purple-200">
                <Crown className="h-4 w-4 text-purple-600 mt-0.5" />
                <span className="text-purple-600 font-medium">
                  <strong>Unlock Premium</strong> to see 5 more demographics and discover your best matches
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Lightbulb className="h-4 w-4 text-yellow-600 mt-0.5" />
                <span className="text-purple-600 font-medium">
                  Get specific grooming & style tips for each demographic
                </span>
              </li>
            </>
          )}
        </ul>
      </Card>
    </div>
  );
};

export default DemographicInsights;